import { AppForm } from '../../../../../../common/forms/app.form';
import { Injectable, Signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  ApplicationAppEntityApplicationRead,
  ApplicationAppEntityApplicationWrite,
  LocationDecisionAppEntityLocationDecisionWrite,
  ParcelAppEntityParcelWrite
} from '../../../../../../openapi';
import { CustomValidator, fromToDate } from '../../../../../../common/forms/custom.validators';
import { toNumber } from '../../../../../../services/helpers';
import { DictionaryCommonService } from '../../../../../../services/dictionary.common.service';
import {
  DictTownRoadItem, DictTownStreetItem,
  DictTownWithRoadsItem,
  DictTownWithStreetsItem
} from '../../../../../../services/dictionary.common.model';
import { toSignal } from '@angular/core/rxjs-interop';

export type TaskNameType =
  'Zabudowy jednorodzinnej'
  | 'Zabudowy wielorodzinnej'
  | 'Obiektu handlowego'
  | 'Obiektu przemysłowego'
  | 'Inne'
  | string;

@Injectable()
export class LocalisationForm extends AppForm {

  public taskNameList: TaskNameType[] = [
    'Zabudowy jednorodzinnej',
    'Zabudowy wielorodzinnej',
    'Obiektu handlowego',
    'Obiektu przemysłowego',
    'Inne'
  ];

  public townWithStreetsList: Signal<DictTownWithStreetsItem[] | undefined> = toSignal(this.dictService.getTownWithStreetsList());

  public townWithRoadsList: Signal<DictTownWithRoadsItem[] | undefined> = toSignal(this.dictService.getTownWithRoadsList());

  public getRoadList(townName?: string | null): Array<DictTownRoadItem> {
    return this.townWithRoadsList()?.filter(v => v.name === townName).at(0)?.roads || [];
  }

  public getStreetList(townName?: string | null): Array<DictTownStreetItem> {
    return this.townWithStreetsList()?.filter(v => v.name === townName)?.at(0)?.streets || [];
  }

  constructor(private dictService: DictionaryCommonService) {
    super({
      city: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      provideRoadNumberAndName: new FormControl(true),

      parcels: new FormControl<ParcelAppEntityParcelWrite[]>(
        [], {
          nonNullable: true,
          validators: [Validators.minLength(1), Validators.maxLength(255)]
        }
      )
    }, {
      validators: [fromToDate('startDate', 'endDate')]
    });
  }

  public get city(): FormControl {
    return this.get('city') as FormControl;
  }

  public cityChange(v?: string | null): void {
    this.street?.setValue('');
    this.street?.markAsUntouched();
  }

  public get provideRoadNumberAndName(): FormControl {
    return this.get('provideRoadNumberAndName') as FormControl;
  }

  public provideRoadNumberAndNameChange(v?: boolean): void {
    this.city.setValue('');
    this.city.markAsUntouched();
    if (v) {
      this.addControl('roadNumber', new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }));

      this.addControl('roadName', new FormControl({
        disabled: true,
        value: null
      }, [Validators.required, Validators.maxLength(255)]));
      this.removeControl('street');
    } else {
      this.removeControl('roadName');
      this.removeControl('roadNumber');
      this.addControl('street', new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }));
    }
  }

  public get roadName(): FormControl | null {
    return this.get('roadName') as FormControl | null;
  }

  public get roadNumber(): FormControl | null {
    return this.get('roadNumber') as FormControl | null;
  }

  public roadNumberChange(roadNumber: string): void {
    const roadName = this.getRoadList(this.city.value).filter(v => v.roadNumber === roadNumber).at(0)?.roadName || '';
    this.roadName?.setValue(roadName);
  }

  public get street(): FormControl | null {
    return this.get('street') as FormControl | null;
  }

  public get leasePurpose(): FormControl {
    return this.get('leasePurpose') as FormControl;
  }

  public createLeasePurposeControl(): void {
    this.addControl('leasePurpose', new FormControl('', [Validators.required, Validators.maxLength(5000)]));
  }

  public get constructionTechnologyDescription(): FormControl | null {
    return this.get('constructionTechnologyDescription') as FormControl | null;
  }

  public createConstructionTechnologyDescription(): void {
    this.addControl('constructionTechnologyDescription', new FormControl(null, [Validators.required, Validators.maxLength(5000)]));
  }

  public get showDateControls(): boolean {
    return !!this.startDate && !!this.endDate;
  }

  public get startDate(): FormControl | null {
    return this.get('startDate') as FormControl | null;
  }

  public get endDate(): FormControl | null {
    return this.get('endDate') as FormControl | null;
  }

  public createDateControls(): void {
    this.addControl('startDate', new FormControl(null, [CustomValidator.isValidDateFormat(), Validators.required, CustomValidator.minDate(new Date())]));
    this.addControl('endDate', new FormControl(null, [CustomValidator.isValidDateFormat(), Validators.required]));
  }

  public get locationDecisions(): FormControl | null {
    return this.get('locationDecisions') as FormControl | null;
  }

  public get locationDecisionsValues(): LocationDecisionAppEntityLocationDecisionWrite[] {
    return this.locationDecisions?.value || [];
  }

  public createLocationDecisionsControl(): void {
    this.addControl('locationDecisions', new FormControl([], {
      nonNullable: true,
      validators: [Validators.maxLength(255)]
    }));
  }

  public get surfaceType(): FormControl | null {
    return this.get('surfaceType') as FormControl | null;
  }

  public createSurfaceTypeControl(): void {
    this.addControl('surfaceType', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
  }

  public get description(): FormControl | null {
    return this.get('description') as FormControl | null;
  }

  public createDescriptionControl(): void {
    this.addControl('description', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
  }

  public taskNameChange(v: TaskNameType): void {
    if (v === 'Inne') {
      this.createDescriptionControl();
    } else {
      this.removeControl('description');
    }
  }

  public get opinionOnTheMatterDesc(): FormControl | null {
    return this.get('opinionOnTheMatterDesc') as FormControl | null;
  }

  public createOpinionOnTheMatterDescControl(): void {
    this.addControl('opinionOnTheMatterDesc', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
  }

  public get objectNameOrDevice(): FormControl | null {
    return this.get('objectNameOrDevice') as FormControl | null;
  }

  public createObjectNameOrDeviceControl(): void {
    this.addControl('objectNameOrDevice', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
  }

  public get objectNameOrAdvertisementDesc(): FormControl | null {
    return this.get('objectNameOrAdvertisementDesc') as FormControl | null;
  }

  public createObjectOrAdvertisementDescControl(): void {
    this.addControl('objectNameOrAdvertisementDesc', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
  }

  public get taskName(): FormControl | null {
    return this.get('taskName') as FormControl | null;
  }

  public createTaskNameControl(): void {
    this.addControl('taskName', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get geometry(): FormControl | null {
    return this.get('geometry') as FormControl | null;
  }

  public createGeometryControl(): void {
    this.addControl('geometry', new FormControl('', { validators: [], nonNullable: true }));
  }

  public get distance(): FormControl | null {
    return this.get('distance') as FormControl | null;
  }

  public createDistanceControl(): void {
    this.addControl('distance', new FormControl('', [Validators.required, Validators.maxLength(255)]));
  }

  public get parcels(): FormControl {
    return this.get('parcels') as FormControl;
  }

  public get parcelsValues(): ParcelAppEntityParcelWrite[] {
    return this.parcels.value;
  }

  public toWriteModel(): ApplicationAppEntityApplicationWrite {
    const value = this.value;
    delete value.parcels;
    delete value?.locationDecisions;
    return {
      ...value,
      distance: toNumber(this.distance?.value),
      roadName: this.roadName?.value || null,
      description:
        this.description?.value
        || this.opinionOnTheMatterDesc?.value
        || this.objectNameOrDevice?.value
        || this.objectNameOrAdvertisementDesc?.value
        || null,
      geometry: this.geometry?.value
    };
  }

  public override patchFormValues(value: ApplicationAppEntityApplicationRead): void {
    this.taskNameChange(value.taskName || '');
    this.description?.setValue(value.description);
    this.opinionOnTheMatterDesc?.setValue(value.description);
    this.objectNameOrDevice?.setValue(value.description);
    this.objectNameOrAdvertisementDesc?.setValue(value.description);
    this.provideRoadNumberAndNameChange(!!value?.provideRoadNumberAndName);

    this.patchValue({
      ...value,
      provideRoadNumberAndName: !!value?.provideRoadNumberAndName,
      city: value?.city || ''
    });
  }

}
